import { Box, Heading, Text } from '@mezzoforte/forge';
import { TimeLeft } from '@/components/Entry/TimeLeft';
import { SidebarHeader } from '@/features/EntryPage/SidebarSection';
import type { EntryTimeLeftStatus } from '@/util/time-left-service';
import { NetAuctionStatus } from '@/types/ListEntry';
import { DateTimeFormat } from '@/util/date';

export interface AuctionStatusProps {
  loading: boolean;
  entryId: number;
  auctionEnd: Date;
  auctionStatus: EntryTimeLeftStatus;
  netAuctionStatus: NetAuctionStatus;
}

interface State {
  title: string;
  description: React.ReactNode;
  bg?: string;
}

const dateOptions: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};

function endedDescription(netAuctionStatus: NetAuctionStatus) {
  if (netAuctionStatus === NetAuctionStatus.SOLD) return 'Myyty';
  if (netAuctionStatus === NetAuctionStatus.ENDED) return 'Päättynyt, tarkistetaan tarjouksia';
  if (netAuctionStatus === NetAuctionStatus.NO_BIDS) return 'Päättynyt, ei tarjouksia';
  if (netAuctionStatus === NetAuctionStatus.APPROVED) return 'Päättynyt, tarjous hyväksytty';
  if (netAuctionStatus === NetAuctionStatus.REJECTED) return 'Päättynyt, tarjous hylätty';
  return 'Päättynyt, tarkistetaan tarjouksia';
}

function AuctionStatus({ title, description, bg }: State) {
  return (
    <SidebarHeader
      bg={bg ?? 'successDark'}
      color="textInvert"
    >
      <Box>
        <Text
          variant="bodySmallBold"
          data-test="auction-status-title"
          mb={1}
        >
          {title}
        </Text>
        <span data-test="auction-status-description">{description}</span>
      </Box>
    </SidebarHeader>
  );
}

export function EntryAuctionStatus({
  entryId,
  loading,
  auctionEnd,
  auctionStatus,
  netAuctionStatus,
}: AuctionStatusProps) {
  function state(): State {
    const endedText = `Huutokauppa päättyi ${DateTimeFormat(dateOptions).format(auctionEnd)}`;

    if (loading && auctionStatus === 'ended')
      return {
        title: endedText,
        description: endedDescription(netAuctionStatus),
        bg: 'subdued',
      };

    if (loading)
      return {
        title: 'Aikaa jäljellä',
        description: (
          <Heading
            variant="h3"
            as="span"
          >
            {' '}
          </Heading>
        ),
      };

    switch (auctionStatus) {
      case 'ended':
        return {
          title: endedText,
          description: endedDescription(netAuctionStatus),
          bg: 'subdued',
        };
      case 'cancelled':
        return {
          title: 'Huutokauppa on peruttu',
          description: '',
          bg: 'subdued',
        };
      case 'continuing':
        return {
          title: endedText,
          description: 'Huutokauppa jatkuu kohta',
          bg: 'subdued',
        };
      case 'final-check':
        return {
          title: endedText,
          description: 'Tarkistetaan viime hetken tarjouksia',
          bg: 'subdued',
        };
      case 'ongoing':
        return {
          title: 'Aikaa jäljellä',
          description: (
            <Heading
              variant="h3"
              as="span"
            >
              <TimeLeft
                id={entryId}
                endsAt={auctionEnd}
              />
            </Heading>
          ),
        };
      case 'scheduled':
        return {
          title: 'Huutokauppa on tulossa',
          description: '',
          bg: 'subdued',
        };
      default:
        return { title: '', description: '' };
    }
  }

  const { title, description, bg } = state();

  return (
    <AuctionStatus
      title={title}
      description={description}
      bg={bg}
    />
  );
}

export function PreviewAuctionStatus() {
  return (
    <AuctionStatus
      title="Ilmoitusta ei ole vielä julkaistu"
      description=""
    />
  );
}
